<template>
  <!-- Het telefoonnummer van een deelnemer: landcode vóór het nummer, in één
       veld (ADR-0099). Wat het doorgeeft is één internationale string
       (`+32 0470 12 34 56`); util/phone.js leest die, zodat het scherm erboven
       het gekozen land niet hoeft te kennen. Alle andere attributen (variant,
       density, id, hide-details) gaan door naar het tekstveld. -->
  <v-text-field
    ref="field"
    v-bind="$attrs"
    :model-value="national"
    :rules="fieldRules"
    :placeholder="placeholder"
    type="tel"
    autocomplete="tel"
    class="phone-field"
    @update:model-value="onNational"
  >
    <template #prepend-inner>
      <v-menu
        v-model="menu"
        :close-on-content-click="false"
        location="bottom start"
        :offset="12"
        @after-enter="focusSearch"
      >
        <template #activator="{ props: activator }">
          <!-- mousedown.stop: anders trekt het tekstveld de focus naar zich
               toe en opent het menu met de cursor in het nummer. -->
          <button
            v-bind="activator"
            type="button"
            class="phone-field__country"
            :aria-label="
              $t('components.phoneField.country_aria', {
                name: selected.name,
                dial: selected.dial,
              })
            "
            @mousedown.stop
          >
            <span class="phone-field__code">{{ selected.code }}</span>
            <span>{{ selected.dial }}</span>
            <v-icon size="18">mdi-chevron-down</v-icon>
          </button>
        </template>

        <div class="phone-field-menu">
          <input
            ref="search"
            v-model="search"
            type="search"
            class="phone-field-menu__search"
            autocomplete="off"
            :placeholder="$t('components.phoneField.search_ph')"
            :aria-label="$t('components.phoneField.search_ph')"
            @keydown.enter.prevent="choose(filtered[0])"
          />
          <div
            class="phone-field-menu__list"
            role="listbox"
            :aria-label="$t('components.phoneField.country_l')"
          >
            <button
              v-for="option in filtered"
              :key="option.code"
              type="button"
              role="option"
              class="phone-field-menu__row"
              :aria-selected="option.code === country ? 'true' : 'false'"
              @click="choose(option)"
            >
              <span>{{ option.name }}</span>
              <span class="phone-field-menu__dial">{{ option.dial }}</span>
            </button>
            <p v-if="!filtered.length" class="phone-field-menu__empty">
              {{ $t("components.phoneField.no_results") }}
            </p>
          </div>
        </div>
      </v-menu>
    </template>
  </v-text-field>
</template>

<script>
import {
  DEFAULT_PHONE_COUNTRY,
  composePhone,
  examplePhone,
  phoneCountries,
  phoneCountryOf,
  splitPhone,
} from "@/util/phone";

/** Kleine letters zonder accenten: "belgie" vindt België. */
const fold = (text) =>
  String(text)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Een nummer dat zelf een land noemt (`+32…`, `0032…`). */
const NAMES_COUNTRY = /^\s*(\+|00)/;

export default {
  name: "PhoneField",
  inheritAttrs: false,
  props: {
    modelValue: { type: String, default: "" },
    rules: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue"],
  data: () => ({
    country: DEFAULT_PHONE_COUNTRY,
    national: "",
    emitted: null,
    menu: false,
    search: "",
  }),
  computed: {
    countries() {
      return phoneCountries(this.$i18n.locale);
    },
    selected() {
      return (
        this.countries.find((option) => option.code === this.country) ?? {
          code: this.country,
          dial: "",
          name: this.country,
        }
      );
    },
    filtered() {
      const query = fold(this.search.trim()).replace(/^\+/, "");
      if (!query) return this.countries;
      return this.countries.filter(
        (option) =>
          fold(option.name).includes(query) ||
          option.code.toLowerCase() === query ||
          option.dial.slice(1).startsWith(query),
      );
    },
    placeholder() {
      const number = examplePhone(this.country);
      return number ? this.$t("components.phoneField.example", { number }) : "";
    },
    /**
     * De regels van het scherm toetsen wat dit veld doorgeeft (landcode plus
     * nummer), niet alleen wat er in het tekstvak staat: `0470 12 34 56` is
     * geldig bij België en niet bij Nederland.
     */
    fieldRules() {
      return this.rules.map((rule) => () => rule(this.composed()));
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        // Onze eigen waarde komt terug via de ouder; die niet opnieuw opknippen,
        // anders herschrijven we wat iemand aan het typen is.
        if (value === this.emitted) return;
        const { country, national } = splitPhone(value, this.country);
        this.country = country;
        this.national = national;
      },
    },
    menu(open) {
      if (open) this.search = "";
    },
  },
  methods: {
    composed() {
      return composePhone(this.country, this.national);
    },
    // Pas na de overgang: eerder staat het menu nog niet in de DOM.
    focusSearch() {
      this.$refs.search?.focus();
    },
    onNational(text) {
      this.national = text ?? "";
      // Wie `+32 470…` plakt, ziet de keuzelijst meespringen naar België.
      if (NAMES_COUNTRY.test(this.national)) {
        const named = phoneCountryOf(this.composed());
        if (named) this.country = named;
      }
      this.emit();
    },
    choose(option) {
      if (!option) return;
      // Noemt het getypte nummer zelf een land, dan geldt na deze keuze de
      // keuzelijst: het nummer houdt zijn cijfers, zonder de landcode ervoor.
      if (NAMES_COUNTRY.test(this.national)) {
        const { national } = splitPhone(this.composed());
        if (!NAMES_COUNTRY.test(national)) this.national = national;
      }
      this.country = option.code;
      this.menu = false;
      this.emit();
      this.$nextTick(() => {
        // Een andere landcode maakt een getypt nummer misschien (on)geldig.
        if (this.national.trim()) this.$refs.field?.validate();
        this.$refs.field?.focus();
      });
    },
    emit() {
      this.emitted = this.composed();
      this.$emit("update:modelValue", this.emitted);
    },
    // Voor een scherm dat zonder geldig nummer niet verder kan: de melding
    // tonen, ook als het veld nog niet aangeraakt is, en het veld in beeld
    // halen. Het midden, want aan de onderrand plakt vaak een footer.
    reveal() {
      const field = this.$refs.field;
      if (!field) return;
      field.validate();
      field.$el.scrollIntoView({ block: "center" });
      field.focus();
    },
  },
};
</script>
